import React from 'react';

export type ToggleState = 'off' | 'on' | 'research';

interface CyclingToggleProps {
  state: ToggleState;
  onChange: (newState: ToggleState) => void;
  disabled?: boolean;
  size?: 'sm' | 'md';
  className?: string;
  'data-testid'?: string;
}

/**
 * CyclingToggle Component
 *
 * A 3-state cycling toggle button that matches DMS Desktop's "Include on Labels" behavior.
 *
 * States:
 * - Off (0): Gray background (#6a6a6a), text "Off"
 * - On (1): Green background (#4caf50), text "✓ On"
 * - Research (2): Orange background (#ff9800), text "✓ Research"
 *
 * Behavior:
 * - Click cycles: Off → On → Research → Off → ...
 * - Emits callback with new state on each click
 * - Keyboard accessible (Enter/Space to toggle)
 * - Touch-friendly (44px min height for mobile)
 */
export const CyclingToggle: React.FC<CyclingToggleProps> = ({
  state,
  onChange,
  disabled = false,
  size = 'md',
  className = '',
  'data-testid': dataTestId,
}) => {
  const handleClick = () => {
    if (disabled) return;

    const stateOrder: ToggleState[] = ['off', 'on', 'research'];
    const currentIndex = stateOrder.indexOf(state);
    const nextIndex = (currentIndex + 1) % stateOrder.length;
    const nextState = stateOrder[nextIndex];

    onChange(nextState);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  // State-specific styles
  // CONTRAST FIX: "Off" state uses white text for better visibility against gray background
  // CONTRAST FIX: "On" state uses darker green (green-700) for better white text contrast in light mode
  const stateStyles = {
    off: 'bg-dark-surface/50 hover:bg-dark-surface/50 active:bg-dark-input text-white border-dark-border/30',
    on: 'bg-green-700 hover:bg-green-600 active:bg-green-800 text-white border-green-700',
    research: 'bg-amber-500 hover:bg-amber-400 active:bg-amber-600 text-white border-amber-500',
  };

  // Size variants
  const sizeStyles = {
    sm: 'px-2 py-1 text-xs min-h-[36px] md:min-h-[28px]',
    md: 'px-3 py-2 text-sm min-h-[44px] md:min-h-[32px]',
  };

  // Disabled styles
  const disabledStyles = disabled
    ? 'opacity-50 cursor-not-allowed'
    : 'cursor-pointer active:scale-95';

  // State text and icon
  const stateContent = {
    off: 'Off',
    on: '✓ On',
    research: '✓ Research',
  };

  const baseStyles = [
    'inline-flex',
    'items-center',
    'justify-center',
    'font-semibold',
    'rounded',
    'border',
    'transition-all',
    'duration-200',
    'focus:outline-none',
    'focus:ring-2',
    'focus:ring-offset-2',
    'focus:ring-offset-dark-bg',
  ].join(' ');

  // Focus ring color based on state
  const focusRingColor = {
    off: 'focus:ring-brand-400',
    on: 'focus:ring-green-500',
    research: 'focus:ring-orange-500',
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      disabled={disabled}
      className={`
        ${baseStyles}
        ${stateStyles[state]}
        ${sizeStyles[size]}
        ${disabledStyles}
        ${focusRingColor[state]}
        ${className}
      `.trim().replace(/\s+/g, ' ')}
      data-testid={dataTestId || "cycling-toggle"}
      data-state={state}
      aria-pressed={state !== 'off'}
      aria-label={`Toggle state: ${stateContent[state]}`}
      data-interaction-id='cycling-toggle'>
      {stateContent[state]}
    </button>
  );
};
